import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {  Admin } from "type/type";
import styled from "styled-components";
import { encryptSHA256, maxLengthCheck, putAdmin } from "api/api";
import { useForm } from "react-hook-form";
import hubLayout from 'styles/css/hubLayout.module.css';
import { useRecoilState } from "recoil";
import { isDarkAtom } from "styles/atoms";
import { AdminLoginInfoContext } from "pages/Layout";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  > div {
    padding: 0.5rem 1rem 1rem;
    &:not(:first-of-type) {
      margin-bottom: 1rem;
      background-color: ${prop => prop.theme.layer3BgColor};
      border-radius: 0.5rem;
    }
  }
  details {
    background-color: ${({ theme }) => theme.newTheme.layer.layer2};
    border-radius: 0.5rem;
    summary {
      background-color: ${({ theme }) => theme.newTheme.root.secondary};
      padding: 0.25rem 1rem;
      border-radius: 0.5rem;
      &::-webkit-details-marker { /* Safari 브라우저용 사용자 정의 스타일 */
        display: none;
      }
      &::marker {
        font-size: 1.5rem;
      }
    }
  }
  .title-h2 {
    font-family: var(--font-pretendard-bold);
    display: inline-flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  label, input[name=text], select, textarea {
    display: inline-block;
    padding: 0.5rem;
  }
  input[name=text], select, textarea {
    border-radius: 0.25rem;
    font-family: var(--font-pretendard-medium);
    &:focus-visible {
      outline: none;
    }
  }
  input[name=text]:disabled {
    background-color: ${prop => prop.theme.readOnlyColor};
    color: ${prop => prop.theme.rootTextColor};
    cursor: auto;
  }
  dd {
    flex: none !important;
  }
  dt {
    width: 130px !important;
  }
`;
const TitleAndBtnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  > div {
    display: flex;
    gap: 0.5rem;
    &:first-of-type {
      flex-direction: column;
    }
    &:last-of-type {
      align-items: center;
    }
  }
`;


function AdminInfo() {
  // 등록/수정 시 전달 값, url
  const navigate = useNavigate();
  const { adminLoginInfo, setAdminLoginInfo } = useContext(AdminLoginInfoContext);
  const [password, setPassword] = useState<string>('');
  const [passwordChk, setPasswordChk] = useState<string>('');
  const [isSamePwd, setIsSamePwd] = useState<boolean>(true);
  const [isPwdChange, setIsPwdChange] = useState<boolean>(false);
  const darkAtom = useRecoilState(isDarkAtom);
  // useForm
  const { register, formState: { errors, isValid }, getValues, trigger } = useForm<any>({
    mode: 'all',
  });

  const [admin, setAdmin] = useState<Admin>(adminLoginInfo);

  const handleChange = (event: any) => {
    const { name, value, checked } = event.target;
    if (name === 'auth_code') {
      setAdmin((prevAdmin) => {
        const currentAuthCode = Array.isArray(prevAdmin.auth_code)
          ? prevAdmin.auth_code
          : []; // 배열로 변환
        const newAuthCode = checked
          ? [...currentAuthCode, value] // 체크되었으면 배열에 추가
          : currentAuthCode.filter((code) => code !== value); // 체크 해제되었으면 배열에서 제거
        return {
          ...prevAdmin,
          auth_code: newAuthCode,
        };
      });
    } else {
      const _value = (name === "approval_status") ? Number(value) : value;
      setAdmin((prevData) => ({
        ...prevData,
        [name]: _value,
      }));
    }
  };

  const handlePwdChange = (event: any) => {
    const { name, value } = event.target;
    if (name === 'password') {
      setPassword(value);
      const encPassword = encryptSHA256(value);
      setAdmin((prevData) => ({
        ...prevData,
        'password': encPassword,
      }));
    } else {
      setPasswordChk(value);
    }
    setIsSamePwd(getValues('password') === getValues('password_chk'));
  };

  function handleToNumericValue(event: any) {
    const input = event.target.value;
    // 숫자만 추출
    const numericValue = input.replace(/[^0-9]/g, "");
    event.target.value = numericValue;
    handleChange(event);
  }

  async function handleIsPwdChange(val: boolean) {
    if (!val) {
      setPassword('');
      setPasswordChk('');
      setIsSamePwd(true);
      setAdmin((prevData) => ({
        ...prevData,
        'password': '',
      }));
    }
    setIsPwdChange(val);
    await trigger();
  }


  async function editAdmin(): Promise<void> {
    const response = await putAdmin(admin);
    if (response.code === 200) {
      setAdminLoginInfo(admin);
      alert(`관리자가 정보가 수정되었습니다.`);
      navigate(-1);
    } else {
      alert(response.message);
    }
  }
  return (
    <>
      <Container>
        <TitleAndBtnContainer>
          <div>
            <p className='title-h2'>
              <span>관리자 수정</span>
            </p>
          </div>
          <div>
            <button type="button" className='default-btn edit-btn' onClick={editAdmin} disabled={!isValid || !isSamePwd}>수정</button>
            <button type="button" className='default-btn list-btn' onClick={() => navigate(-1)}>이전</button>
          </div>
        </TitleAndBtnContainer>
        <div className={`${hubLayout.acc__cnt}`}>
          <div className={`${hubLayout.acc_item_info}`}>
            <dl>
              <dt><label>ID</label></dt>
              <dd className={`${hubLayout.display_flex}`}>
                <input type="text" value={admin.id} placeholder="ID를 입력하세요." {...register("id")} disabled/>
              </dd>
              {!isPwdChange ?
                <>
                  <dt><label></label></dt>
                  <dd className={`${hubLayout.display_flex}`}>
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue} ${hubLayout.width_100}`} onClick={() => handleIsPwdChange(true)}>비밀번호 변경</button>
                  </dd>
                </>
                :
                <>
                  <dt><label></label></dt>
                  <dd className={`${hubLayout.display_flex}`}>
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue} ${hubLayout.width_150}`} onClick={() => handleIsPwdChange(false)}>비밀번호 변경 취소</button>
                  </dd>
                </>
              }
              <dt></dt><dd></dd>
            </dl>
            <dl style={{ marginBottom: '5px' }}>
              {isPwdChange &&
                  <>
                    <dt><label>비밀번호</label></dt>
                    <dd>
                      <input type="password" value={password} placeholder="비밀번호를 입력해주세요." className={`${errors.password && hubLayout.border_red}`}
                        {...register('password', {
                          required: '비밀번호를 입력하세요.',
                          minLength: {
                            value: 8,
                            message: '비밀번호를 8자 이상 입력하세요.',
                          },
                          maxLength: {
                            value: 15,
                            message: '비밀번호를 15자 이하로 입력하세요.',
                          },
                          pattern: {
                            value: /^(?=.*[0-9])(?=.*[a-zA-Z])(?=.*[~!@#$%^*()[\]{}_+|;/:=.,]).*$/,
                            message: '비밀번호를 영문+숫자+특수문자 조합으로 입력하세요.',
                          },
                          onChange: handlePwdChange
                        })}
                      />
                    </dd>
                    <dt><label>비밀번호 확인</label></dt>
                    <dd>
                      <input type="password" value={passwordChk} placeholder="비밀번호를 한번 더 입력해 주세요." className={`${errors.password_chk && hubLayout.border_red}`}
                        {...register("password_chk", {
                          required: true,
                          onChange: handlePwdChange
                        })}
                      />
                    </dd>
                    {!isSamePwd && <span className={`${hubLayout.tx_red}`} style={{ color: darkAtom ? '#fff' : '#000' }}>* 비밀번호가 일치하지 않습니다.</span>}
                  </>
                }
            </dl>
            {isPwdChange && <p className={`${errors.password && hubLayout.tx_red}`} style={{ color: darkAtom ? '#fff' : '#000', paddingLeft: '1rem' }}>* 비밀번호를 영문+숫자+특수문자 조합으로 8~15자 입력해주세요.</p>}
            <dl>
              <dt><label>이름</label></dt>
              <dd className={`${hubLayout.display_flex}`}>
                <input type="text" value={admin.name} placeholder="이름을 입력해주세요." className={`${errors.name && hubLayout.border_red}`}
                  {...register("name", {
                    required: {
                      value: true,
                      message: '이름을 입력해주세요',
                    },
                    onChange: handleChange
                  })}
                />
              </dd>
              <dt><label>연락처</label></dt>
              <dd className={`${hubLayout.display_flex}`}>
                <input type="text" value={admin.mobile} placeholder="연락처를 입력해주세요." className={`${errors.mobile && hubLayout.border_red}`} maxLength={11}
                  {...register("mobile", {
                    required: {
                      value: true,
                      message: '연락처를 입력해주세요',
                    },
                    pattern : {
                      value: /^01[016789]\d{3,4}\d{4}$/g,
                      message: `정확한 연락처를 입력해주세요.`,
                    },
                    onChange: (event) => {
                      handleToNumericValue(event);
                      maxLengthCheck(event);
                    }
                  })}
                />
              </dd>
              <dt><label>E-MAIL</label></dt>
              <dd className={`${hubLayout.display_flex}`}>
                <input type="text" value={admin.email} placeholder="E-MAIL을 입력해주세요." className={`${errors.email && hubLayout.border_red}`}
                  {...register("email", {
                    required: {
                      value: true,
                      message: 'E-MAIL을 입력해주세요',
                    },
                    onChange: handleChange
                  })}
                />
              </dd>
              <dt></dt><dd></dd>
            </dl>
            {/* <dl>
              <dt><label>승인상태</label></dt>
              <dd className={`${hubLayout.display_flex}`}>
                <span className={`${hubLayout.margin_right_10}`}>
                  <input type="radio" className={`${hubLayout.radio}`} value="0" id='inp-approval-wait' checked={admin.approval_status === 0} style={{ top: '7px' }}
                    {...register(`approval_status`, {
                      required: true,
                      onChange: handleChange
                    })}
                  />
                  <label htmlFor='inp-approval-wait' style={{ color: darkAtom ? '#fff' : '' }}>승인대기</label>
                </span>
                <span className={`${hubLayout.margin_right_10}`}>
                  <input type="radio" className={`${hubLayout.radio}`} value="1" id='inp-approval' checked={admin.approval_status === 1} style={{ top: '7px' }}
                    {...register(`approval_status`, {
                      required: true,
                      onChange: handleChange
                    })}
                  />
                  <label htmlFor='inp-approval' style={{ color: darkAtom ? '#fff' : '' }}>승인</label>
                </span>
                <span className={`${hubLayout.margin_right_10}`}>
                  <input type="radio" className={`${hubLayout.radio}`} value="2" id='inp-not-approval' checked={admin.approval_status === 2} style={{ top: '7px' }}
                    {...register(`approval_status`, {
                      required: true,
                      onChange: handleChange
                    })}
                  />
                  <label htmlFor='inp-not-approval' style={{ color: darkAtom ? '#fff' : '' }}>미승인</label>
                </span>
              </dd>
            </dl> */}
          </div>
        </div>

        {/* 권한 */}
        {/* <details>
          <summary>
            <p className='title-h2'>
              <FiInfo />
              <span>권한</span>
            </p>
          </summary>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.acc_item_info}`}>
              <dl>
                <dd className={`${hubLayout.display_flex}`}>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="A" id='inp-auth-code-A' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("A")} 
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-A' style={{ color: darkAtom ? '#fff' : '' }}>고객관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="B" id='inp-auth-code-B' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("B")} 
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-B' style={{ color: darkAtom ? '#fff' : '' }}>장비관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="C" id='inp-auth-code-C' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("C")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-C' style={{ color: darkAtom ? '#fff' : '' }}>계약관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="D" id='inp-auth-code-D' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("D")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-D' style={{ color: darkAtom ? '#fff' : '' }}>공지사항</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="E" id='inp-auth-code-E' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("E")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-E' style={{ color: darkAtom ? '#fff' : '' }}>관리자 관리</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="checkbox" value="F" id='inp-auth-code-F' className={`${hubLayout.check}`}
                      checked={Array.isArray(admin.auth_code) && admin.auth_code.includes("F")}
                      {...register(`auth_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-auth-code-F' style={{ color: darkAtom ? '#fff' : '' }}>관리자 권한 관리</label>
                  </span>
                </dd>
              </dl>
            </div>
          </div>
        </details> */}
      </Container>
    </>
  );
}
export default AdminInfo;